import {ApolloError} from "@apollo/client";
import {TFunction} from "i18next";

/**
 * Formats tenant creation errors with user-friendly messages
 *
 * Categorizes errors into:
 * - Network errors: Connection issues, timeouts
 * - GraphQL errors: Backend/API errors with error details
 * - Unknown errors: Other unexpected failures
 *
 * @param error - The error from tenant creation (ApolloError or generic Error)
 * @param tenantName - The name of the tenant being created
 * @param t - Translation function for i18n
 * @returns A new Error with a user-friendly message
 */
export const formatTenantCreationError = (
  error: ApolloError | Error,
  tenantName: string,
  t: TFunction
): Error => {
  // Handle Apollo errors
  if (error instanceof ApolloError) {
    // Network errors (timeout, connection issues, etc.)
    if (error.networkError) {
      return new Error(
        t("create-organization.errors.tenant.network-error-detail")
      );
    }

    // GraphQL errors from backend
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      const graphQLError = error.graphQLErrors[0];
      const errorMessage = graphQLError?.message || error.message;
      return new Error(
        t("create-organization.errors.tenant.graphql-error-detail", {
          errorMessage
        })
      );
    }
  }

  // Default/unknown error
  return new Error(
    t("create-organization.errors.tenant.creation-failed-detail", {
      tenantName
    })
  );
};
